import { useCallback, useRef, useState } from "react";
import { type Identifier, useDataProvider, useGetList } from "ra-core";

import {
  AMOUNT_RANGE_MESSAGE,
  BUDGET_ORDER_MESSAGE,
  DEAL_ID_IMMUTABLE_MESSAGE,
  DUPLICATE_ELEMENT_MESSAGE,
  KEY_IMMUTABLE_MESSAGE,
  LABEL_DUPLICATE_MESSAGE,
  LABEL_REQUIRED_MESSAGE,
  LABEL_TOO_LONG_MESSAGE,
  LEAD_WRITE_DENIED_MESSAGE,
  MAX_OPTIONS_MESSAGE,
  NOTES_TOO_LONG_MESSAGE,
  OPTION_ARCHIVED_MESSAGE,
  OPTION_NOT_FOUND_MESSAGE,
  PROFILE_ALREADY_EXISTS_MESSAGE,
  PROJECT_ARCHIVED_MESSAGE,
  ZONE_TOO_LONG_MESSAGE,
} from "./profileRules";
import type { CrmDataProvider } from "../providers/types";
import type { DealProfile } from "../types";

/**
 * Every Spanish guard message `profileRules.ts` exports (server + client
 * validators) -- the only messages `saveField`'s catch block is allowed to
 * surface verbatim. Anything else (an offline "Failed to fetch", a raw
 * database/RLS error, ...) falls back to `DEFAULT_SAVE_ERROR_MESSAGE`
 * instead of leaking an English or technical string into the red
 * `StatusPill`.
 */
const KNOWN_GUARD_MESSAGES: ReadonlySet<string> = new Set([
  OPTION_NOT_FOUND_MESSAGE,
  OPTION_ARCHIVED_MESSAGE,
  PROJECT_ARCHIVED_MESSAGE,
  DUPLICATE_ELEMENT_MESSAGE,
  MAX_OPTIONS_MESSAGE,
  DEAL_ID_IMMUTABLE_MESSAGE,
  PROFILE_ALREADY_EXISTS_MESSAGE,
  KEY_IMMUTABLE_MESSAGE,
  LABEL_DUPLICATE_MESSAGE,
  LABEL_REQUIRED_MESSAGE,
  LABEL_TOO_LONG_MESSAGE,
  AMOUNT_RANGE_MESSAGE,
  BUDGET_ORDER_MESSAGE,
  ZONE_TOO_LONG_MESSAGE,
  NOTES_TOO_LONG_MESSAGE,
  LEAD_WRITE_DENIED_MESSAGE,
]);

const PROFILE_SORT = { field: "id", order: "ASC" as const };
const SINGLE_ROW_PAGINATION = { page: 1, perPage: 1 } as const;

export type DealProfileSaveStatus = "idle" | "saving" | "saved" | "error";

export interface UseDealProfileSaveResult {
  /** `undefined` while the first read is in flight, `null` when the lead has no profile yet. */
  profile: DealProfile | null | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
  saveStatus: DealProfileSaveStatus;
  /** The failed save's message (server guard or network fallback); `null` otherwise. */
  errorMessage: string | null;
  /**
   * Saves `patch` (only the changed field(s)) against `dealId`'s profile.
   * Creates the row lazily on the first save; on a create race (two saves
   * landing before the row existed) it refetches the winning row and applies
   * `patch` as an update instead, so the caller never sees
   * "Este lead ya tiene un perfil". Rejects on failure so the caller can
   * revert its optimistic UI.
   */
  saveField: (patch: Partial<DealProfile>) => Promise<void>;
}

const DEFAULT_SAVE_ERROR_MESSAGE = "No se pudo guardar. Intenta de nuevo.";

/**
 * Almikia Epic EF (TASK-119), DATA CONTRACT EF (2)/(6): the buyer profile's
 * data + autosave layer for a single lead, shared by every mount of
 * `DealProfileSection` (lead show today, the Hoy sheet in TASK-122
 * tomorrow) -- only `dealId` is a structural requirement.
 */
export function useDealProfileSave(
  dealId: Identifier,
): UseDealProfileSaveResult {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const {
    data: rows,
    isPending,
    error,
    refetch,
  } = useGetList<DealProfile>("deal_profiles", {
    filter: { deal_id: dealId },
    pagination: SINGLE_ROW_PAGINATION,
    sort: PROFILE_SORT,
  });
  const profile = isPending ? undefined : (rows?.[0] ?? null);

  // Mutable across renders (not state): the profile id is read/written
  // synchronously inside a single `saveField` call, and must survive two
  // overlapping calls (the create-race case) without waiting on a re-render.
  const profileIdRef = useRef<Identifier | null>(profile?.id ?? null);
  // `dealId` changing (the same mounted `DealProfileSection` moving from one
  // lead to another) must never let a stale id from the PREVIOUS lead survive
  // into the next save -- otherwise that save lands as an UPDATE against the
  // wrong lead's profile. `useGetList`'s query key already changes with
  // `dealId`, but the ref itself is only ever written from `profile?.id`
  // below, so it needs its own explicit reset the moment `dealId` changes.
  const dealIdRef = useRef(dealId);
  if (dealIdRef.current !== dealId) {
    dealIdRef.current = dealId;
    profileIdRef.current = null;
  }
  if (profile?.id != null && profile.deal_id === dealId) {
    profileIdRef.current = profile.id;
  }

  const [saveStatus, setSaveStatus] = useState<DealProfileSaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveField = useCallback(
    async (patch: Partial<DealProfile>) => {
      setSaveStatus("saving");
      setErrorMessage(null);
      try {
        if (profileIdRef.current == null) {
          try {
            const { data: created } = await dataProvider.create(
              "deal_profiles",
              { data: { deal_id: dealId, ...patch } },
            );
            profileIdRef.current = created.id;
          } catch (createError) {
            if (
              createError instanceof Error &&
              createError.message === PROFILE_ALREADY_EXISTS_MESSAGE
            ) {
              // A concurrent save already created the row: recover its id
              // and apply this save as an update, so the user never sees
              // "Este lead ya tiene un perfil" (DATA CONTRACT EF (6)).
              const { data: existingRows } = await dataProvider.getList(
                "deal_profiles",
                {
                  filter: { deal_id: dealId },
                  pagination: SINGLE_ROW_PAGINATION,
                  sort: PROFILE_SORT,
                },
              );
              const existingProfile = existingRows[0] as
                | DealProfile
                | undefined;
              if (!existingProfile) throw createError;
              profileIdRef.current = existingProfile.id;
              await dataProvider.update("deal_profiles", {
                id: existingProfile.id,
                data: patch,
                previousData: existingProfile,
              });
            } else {
              throw createError;
            }
          }
        } else {
          await dataProvider.update("deal_profiles", {
            id: profileIdRef.current,
            data: patch,
            // `profile` briefly lags a just-recovered id (before the next
            // refetch resolves) -- the fallback carries `deal_id` too (not
            // just `id`), since the FakeRest `beforeUpdate` hook looks the
            // lead up through `previousData.deal_id` and a fast save made
            // before that refetch settles must not fail with "No
            // encontramos este lead".
            previousData: (profile?.deal_id === dealId
              ? profile
              : ({
                  id: profileIdRef.current,
                  deal_id: dealId,
                } as Partial<DealProfile>)) as DealProfile,
          });
        }
        setSaveStatus("saved");
        refetch();
      } catch (err) {
        setSaveStatus("error");
        // Only a known Spanish guard message (from `profileRules.ts`) is
        // ever shown verbatim -- anything else (an offline "Failed to
        // fetch", a raw database/RLS error) would leak English or
        // technical detail into the red `StatusPill`, so it falls back to
        // the generic Spanish message instead.
        setErrorMessage(
          err instanceof Error && KNOWN_GUARD_MESSAGES.has(err.message)
            ? err.message
            : DEFAULT_SAVE_ERROR_MESSAGE,
        );
        throw err;
      }
    },
    [dataProvider, dealId, profile, refetch],
  );

  return {
    profile,
    isPending,
    error,
    refetch,
    saveStatus,
    errorMessage,
    saveField,
  };
}
